import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCustomerReconciliation, type CustomerReconciliation } from '../api/client';

export function ReconciliationWarning({ data, compact = false }: { data?: CustomerReconciliation | null; compact?: boolean }) {
  const { t } = useTranslation();
  if (!data || (data.complete && Math.abs(data.discrepancy || 0) <= 0.01)) return null;
  const money = (value: number | null) => value == null ? t('documents.openAmountUnavailable', 'Unavailable') : new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(value);
  if (compact) return <p role="alert" className="mt-1 max-w-52 text-xs text-amber-800">
    {data.complete ? `${t('collectionWorkflow.discrepancy')}: ${money(data.discrepancy)}` : t('collectionWorkflow.incomplete')}
  </p>;
  return <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert">
    <strong>{t('collectionWorkflow.discrepancy')}</strong>
    <p>{t('collectionWorkflow.account')}: {money(data.authoritative_balance)}</p>
    <p>{t('collectionWorkflow.rawTotal')}: {money(data.raw_open_total)}</p>
    <p>{t('collectionWorkflow.difference')}: {money(data.discrepancy)}</p>
    {!data.complete && <p>{t('collectionWorkflow.incomplete')}</p>}
    {data.unknown_count > 0 && <p>{t('invoiceReport.uncertainBalanceWarning', {
      count: data.unknown_count,
      defaultValue: '{{count}} invoice balances could not be verified and are excluded from totals.',
    })}</p>}
  </div>;
}

export default function CustomerReconciliationPanel({ customerId }: { customerId: number }) {
  const { t } = useTranslation();
  const [data, setData] = useState<CustomerReconciliation | null>(null);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setData(null);
    setError(false);
    getCustomerReconciliation(customerId).then(response => { if (active) setData(response.data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [customerId, revision]);
  if (error) return <div role="alert" className="text-amber-800">{t('collectionWorkflow.incomplete')} <button onClick={() => setRevision(value => value + 1)}>{t('common.retry', 'Retry')}</button></div>;
  if (!data || (data.complete && Math.abs(data.discrepancy || 0) <= 0.01)) return null;
  return <section className="space-y-2" data-testid="customer-reconciliation">
    <ReconciliationWarning data={data} />
    <p className="text-xs">{t('collectionWorkflow.investigate')}</p>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr>{['documents.docNumber', 'documents.date', 'documents.amount', 'collectionWorkflow.rawBalance', 'collectionWorkflow.adjustment', 'collectionWorkflow.effective'].map(label => <th key={label} className="p-2 text-start">{t(label)}</th>)}</tr></thead>
        <tbody>{data.documents.map(doc => <tr key={`${doc.document_type}:${doc.document_number}`}>
          <td className="p-2">{doc.document_link ? <a href={doc.document_link} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">{doc.document_number}</a> : doc.document_number}</td>
          <td className="p-2">{doc.document_date}</td>
          {[doc.original_amount, doc.raw_remaining, doc.adjustment, doc.effective_remaining].map((amount, index) => <td className="p-2" key={index}>{amount == null ? t('documents.openAmountUnavailable', 'Unavailable') : amount.toFixed(2)}</td>)}
        </tr>)}</tbody>
      </table>
    </div>
  </section>;
}
